import dashboards from '../../api/dashboards';
import * as types from '../mutation-types';
import _ from 'lodash';
import moment from 'moment';
import colors from 'vuetify/lib/util/colors';

const ABF_START_DATE = '2016-06-20';

const state = {
  platform: {
    stats: {
      headline: {
        registrations: 0,
        enquiries: 0,
        avg_funding_requested: 0,
        avg_funding_obtained: 0,
        total_funding_obtained: 0
      },
      registrations: [
        {
          date: ABF_START_DATE,
          registrations: 0
        }
      ],
      enquiries: {
        new: [
          {
            date: ABF_START_DATE,
            enquiries: 0,
            avg_funding_requested: 0
          }
        ],
        funded: [
          {
            date: ABF_START_DATE,
            avg_funding_obtained: 0,
            total_funding_obtained: 0
          }
        ]
      }
    },
    previousStats: {
      headline: {
        registrations: 0,
        enquiries: 0,
        avg_funding_requested: 0,
        avg_funding_obtained: 0,
        total_funding_obtained: 0
      },
      registrations: [
        {
          date: ABF_START_DATE,
          registrations: 0
        }
      ],
      enquiries: {
        new: [
          {
            date: ABF_START_DATE,
            enquiries: 0,
            avg_funding_requested: 0
          }
        ],
        funded: [
          {
            date: ABF_START_DATE,
            avg_funding_obtained: 0,
            total_funding_obtained: 0
          }
        ]
      }
    },
    enquiries: {
      pending: 0,
      sme_cancelled: 0,
      enquiry_accepted: 0,
      enquiry_rejected: 0,
      expired: 0,
      funder_offer_made: 0,
      funder_declined_to_offer: 0,
      sme_offer_accepted: 0,
      sme_offer_rejected: 0,
      funding_released: 0
    }
  },
  partner: {
    stats: {
      referrals: 0,
      registrations: 0,
      enquiriesSent: 0,
      enquiriesAccepted: 0,
      SMEsFunded: 0,
      averageAmountRequested: 0,
      averageAmountFunded: 0,
      totalFundsReleased: 0
    },
    enquiriesByCompanyType: {},
    enquiriesByProductType: {},
    referralsByCreditProfile: {},
    monthlyFundedReferrals: {}
  },
  funder: {
    fundingReleased: 0,
    enquiries: {
      'AUTO CLOSED': 0,
      'ENQUIRY ACCEPTED': 0,
      'ENQUIRY REJECTED': 0,
      EXPIRED: 0,
      'FUNDER DECLINED TO OFFER': 0,
      'FUNDER OFFER MADE': 0,
      'FUNDING RELEASED': 0,
      PENDING: 0,
      'SME CANCELLED': 0,
      'SME OFFER ACCEPTED': 0,
      'SME OFFER REJECTED': 0
    },
    pastYear: {},
    monthlyReferrals: {}
  },
  dashboardDataLoading: true
};

/**
 * Previous* getters are dynamically called by the DashboardPlatformChartData getter.
 * Don't delete them!
 */
const getters = {
  DashboardDataLoading: state => state.dashboardDataLoading,
  DashboardPlatformHeadlines: state => state.platform.stats.headline,
  DashboardPlatformEnquiries: state => state.platform.enquiries,
  DashboardPlatformRegistrations: state =>
    _.map(state.platform.stats.registrations, i => {
      return { t: moment(i.date), y: i.registrations };
    }),
  DashboardPlatformNewEnquiriesCount: state =>
    _.map(state.platform.stats.enquiries.new, i => {
      return { t: moment(i.date), y: i.enquiries };
    }),
  DashboardPlatformNewEnquiriesAvgRequested: state =>
    _.map(state.platform.stats.enquiries.new, i => {
      return { t: moment(i.date), y: i.avg_funding_requested };
    }),
  DashboardPlatformFundedEnquiriesAvg: state =>
    _.map(state.platform.stats.enquiries.funded, i => {
      return { t: moment(i.date), y: i.avg_funding_obtained };
    }),
  DashboardPlatformFundedEnquiriesCount: state =>
    _.map(state.platform.stats.enquiries.funded, i => {
      return { t: moment(i.date), y: i.total_funding_obtained };
    }),
  PreviousDashboardPlatformHeadlines: state => state.platform.previousStats.headline,
  PreviousDashboardPlatformRegistrations: state =>
    _.map(state.platform.previousStats.registrations, i => {
      return { t: moment(i.date), y: i.registrations };
    }),
  PreviousDashboardPlatformNewEnquiriesCount: state =>
    _.map(state.platform.previousStats.enquiries.new, i => {
      return { t: moment(i.date), y: i.enquiries };
    }),
  PreviousDashboardPlatformNewEnquiriesAvgRequested: state =>
    _.map(state.platform.previousStats.enquiries.new, i => {
      return { t: moment(i.date), y: i.avg_funding_requested };
    }),
  PreviousDashboardPlatformFundedEnquiriesAvg: state =>
    _.map(state.platform.previousStats.enquiries.funded, i => {
      return { t: moment(i.date), y: i.avg_funding_obtained };
    }),
  PreviousDashboardPlatformFundedEnquiriesCount: state =>
    _.map(state.platform.previousStats.enquiries.funded, i => {
      return { t: moment(i.date), y: i.total_funding_obtained };
    }),
  DashboardPlatformChartData: (state, getters) => getter => {
    return {
      datasets: [
        {
          label: 'Current',
          data: getters[getter],
          fill: false,
          borderColor: '#2196F3'
        },
        {
          label: 'Previous',
          data: getters[`Previous${getter}`],
          borderDash: [5, 5],
          pointRadius: 2,
          fill: false,
          borderColor: '#BDBDBD'
        }
      ]
    };
  },
  DashboardPartnerStats: state => state.partner.stats,
  DashboardPartnerEnquiriesByProductType: state => {
    return {
      datasets: [
        {
          backgroundColor: [colors.deepPurple.base, colors.deepPurple.lighten4],
          data: Object.values(state.partner.enquiriesByCompanyType)
        }
      ],
      labels: Object.keys(state.partner.enquiriesByCompanyType)
    };
  },
  DashboardPartnerReferralsByCreditProfile: state => {
    const scoreColours = {
      'Exceeds Maximum Risk': '#D71211',
      'Maximum Risk': '#D71211',
      'High Risk': '#EA6700',
      'Above Average Risk': '#F1B402',
      'Below Average Risk': '#A3C102',
      'Low Risk': '#5BBA04',
      'Very Low Risk': '#059700'
    };

    const data = state.partner.referralsByCreditProfile;

    return {
      datasets: [
        {
          backgroundColor: Object.keys(data).map(label => scoreColours[label]),
          data: Object.values(data)
        }
      ],
      labels: Object.keys(data)
    };
  },
  DashboardPartnerMonthlyFundedReferrals: state => {
    const data = state.partner.monthlyFundedReferrals;

    return {
      datasets: [
        {
          backgroundColor: [
            colors.purple.lighten2,
            colors.deepPurple.lighten2,
            colors.indigo.lighten2,
            colors.orange.lighten2,
            colors.amber.lighten2,
            colors.yellow.lighten2,
            colors.blue.lighten2,
            colors.lightBlue.lighten2,
            colors.cyan.lighten2
          ],
          data: Object.values(data)
        }
      ],
      labels: Object.keys(data)
    };
  },
  DashboardPartnerHasSubAccounts: state => !!state.partner.hasOwnProperty('subAccounts'),
  DashboardPartnerSubAccountStats: state => {
    if (state.partner.hasOwnProperty('subAccounts')) {
      const subAccounts = state.partner.subAccounts;
      const subAccountStats = Object.values(subAccounts).map(subAccount => subAccount.stats);
      return _.mergeWith({}, ...subAccountStats, (objValue, srcValue) =>
        Number(objValue) ? objValue + srcValue : srcValue
      );
    } else {
      return {};
    }
  },

  DashboardFunderValueFunded: state => state.funder.fundingReleased,
  DashboardFunderEnquiryBreakdown: state => state.funder.enquiries,
  DashboardFunderEnquiriesByMonth: state => {
    const data = state.funder.pastYear;

    return {
      datasets: [
        {
          backgroundColor: [
            colors.red.lighten2,
            colors.pink.lighten2,
            colors.purple.lighten2,
            colors.deepPurple.lighten2,
            colors.indigo.lighten2,
            colors.orange.lighten2,
            colors.amber.lighten2,
            colors.yellow.lighten2,
            colors.blue.lighten2,
            colors.lightBlue.lighten2,
            colors.cyan.lighten2,
            colors.green.lighten2
          ],
          data: Object.values(data)
        }
      ],
      labels: Object.keys(data)
    };
  }
};

const actions = {
  getPlatformData({ commit }, { startDate, endDate }) {
    commit(types.SET_DASHBOARD_DATA_LOADING, true);
    dashboards.getPlatformData(startDate, endDate).then(platformData => {
      commit(types.SET_PLATFORM_DASHBOARD_DATA, platformData);
      commit(types.SET_DASHBOARD_DATA_LOADING, false);
    });
  },
  getPartnerData({ commit }, { startDate, endDate, partnerId = null }) {
    commit(types.SET_DASHBOARD_DATA_LOADING, true);
    dashboards.getPartnerData(startDate, endDate, partnerId).then(partnerData => {
      commit(types.SET_PARTNER_DASHBOARD_DATA, partnerData);
      commit(types.SET_DASHBOARD_DATA_LOADING, false);
    });
  },
  async getFunderDashboardData({ commit }, { startDate, endDate, funderId = null }) {
    commit(types.SET_DASHBOARD_DATA_LOADING, true);
    const funderData = await dashboards.getFunderData(startDate, endDate, funderId);
    commit(types.SET_FUNDER_DASHBOARD_DATA, funderData);
    commit(types.SET_DASHBOARD_DATA_LOADING, false);
  }
};

const mutations = {
  [types.SET_PLATFORM_DASHBOARD_DATA](state, platformData) {
    state.platform = platformData;
  },
  [types.SET_PARTNER_DASHBOARD_DATA](state, partnerData) {
    state.partner = partnerData;
  },
  [types.SET_DASHBOARD_DATA_LOADING](state, isLoading) {
    state.dashboardDataLoading = isLoading;
  },
  [types.SET_FUNDER_DASHBOARD_DATA](state, funderData) {
    const { fundingReleased, enquiries, pastYear } = funderData;

    state.funder = {
      fundingReleased,
      enquiries: _.merge({}, state.funder.enquiries, enquiries),
      pastYear
    };
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
